import React from "react";
import { motion } from "framer-motion";
import {
  Compass,
  ShieldCheck,
  FileText,
  Network,
  Eye,
  KeyRound,
  AlertTriangle,
  Workflow,
  ArrowRight,
} from "lucide-react";

const services = [
  {
    icon: Compass,
    eyebrow: "PILIER 01",
    title: "Gouvernance & gestion des risques",
    desc: "Politiques de sécurité robustes alignées avec les standards internationaux. De l'analyse de risque à la mise en conformité.",
    items: [
      { icon: FileText, label: "Analyse de risques EBIOS RM & ISO/IEC 27005" },
      { icon: ShieldCheck, label: "Certification ISO 27001 — audit, SMSI, déploiement" },
      { icon: Workflow, label: "Conformité RGPD, NIS2, secteur régulé" },
    ],
    image:
      "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650",
    testId: "service-card-governance",
  },
  {
    icon: ShieldCheck,
    eyebrow: "PILIER 02",
    title: "Cybersécurité défensive & opérations",
    desc: "Sécurisation technique et opérationnelle des systèmes d'information. Détection, durcissement, réponse à incident.",
    items: [
      { icon: Network, label: "SOC / SIEM / EDR — mise en place & run" },
      { icon: Eye, label: "Blue teaming, threat hunting & durcissement" },
      { icon: KeyRound, label: "Gestion des accès à privilèges (PAM)" },
      { icon: AlertTriangle, label: "Réponse à incident & amélioration continue" },
    ],
    image:
      "https://images.pexels.com/photos/1181320/pexels-photo-1181320.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650",
    testId: "service-card-defensive",
  },
];

export default function ServicesSection() {
  return (
    <section id="services" className="relative py-24 md:py-32 diag-stripes">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono text-xs text-emerald-400 mb-4 tracking-widest">
              // NOS SERVICES
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
              Une double expertise.
              <br />
              <span className="italic text-emerald-400">Une posture unique.</span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-gray-400 leading-relaxed">
              Deux piliers complémentaires pour couvrir l'intégralité du cycle de vie
              cybersécurité : du cadre stratégique aux opérations 24/7.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={s.testId}
              className="group relative rounded-3xl bg-[#0C1410] border border-[#1A2E22] hover:border-emerald-500/40 transition-all p-7 md:p-9 overflow-hidden hover:-translate-y-1 hover:shadow-[0_24px_60px_-20px_rgba(16,185,129,0.25)]"
            >
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />

              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="font-mono text-[11px] text-emerald-400 tracking-widest">
                    {s.eyebrow}
                  </div>
                  <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                    <s.icon className="w-5 h-5" />
                  </div>
                </div>

                <h3 className="font-display text-2xl md:text-3xl font-semibold leading-tight mb-4">
                  {s.title}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-7">{s.desc}</p>

                <div className="relative rounded-2xl overflow-hidden border border-white/5 mb-7">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-48 object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C1410] via-transparent to-transparent" />
                </div>

                <ul className="space-y-3">
                  {s.items.map((it, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 text-sm text-gray-300"
                    >
                      <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#121C16] border border-[#1A2E22] text-emerald-400 shrink-0">
                        <it.icon className="w-3.5 h-3.5" />
                      </span>
                      <span>{it.label}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  data-testid={`${s.testId}-cta`}
                  className="mt-8 inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium group/cta"
                >
                  Discuter de ce volet
                  <ArrowRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-1" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
